import {Modal, Text} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../modals/generic_modal_conductor';
import {ModalProps} from '../../modals/types';

export type ChangeAgentConfirmationModalPayload = {
	onConfirm: () => void;
	// Called whichever way the dialog goes - confirmed, cancelled, dismissed, or closed by the
	// conductor itself on Escape or browser back. The caller uses it to stop believing the dialog
	// is still open, so that something closing it from the outside (a permission going away
	// mid-decision) does not close whatever took its place instead.
	onClose?: () => void;
};

export const showChangeAgentConfirmationModal = (props: ChangeAgentConfirmationModalPayload): void => {
	showModal({
		type: MODAL_TYPE.CHANGE_AGENT_CONFIRMATION_MODAL,
		...props,
		// Handed to the conductor rather than called from the dialog's own buttons: the conductor
		// runs it on every way a dialog closes, Escape and browser back included, which the buttons
		// never see.
		onCloseModal: props.onClose,
	});
};

export const ChangeAgentConfirmationModal = ({
	closeModal,
	onConfirm,
}: ModalProps<ChangeAgentConfirmationModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();

	// The conductor runs onClose as it closes the dialog - see the show helper above.
	const dismiss = (): void => {
		closeModal();
	};

	const handleConfirm = (): void => {
		onConfirm();
		dismiss();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={dismiss} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'nova_invocation.change_agent.confirm_title'})}</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'nova_invocation.change_agent.confirm_body'})}</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={handleConfirm} dataCy="change-agent-confirm">
					{formatMessage({id: 'nova_invocation.change_agent.confirm_cta'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={dismiss} dataCy="change-agent-cancel">
					{formatMessage({id: 'nova_invocation.change_agent.keep_conversation'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
